import { InventoryItem } from '@/types/inventory';
import { supabase } from '@/integrations/supabase/client';

/**
 * Fast inventory search service using database-side relevance scoring
 * This replaces the complex two-pass client-side approach with a single efficient query
 */

// Simple in-memory cache for search results
const searchCache = new Map<string, { results: InventoryItem[]; timestamp: number; lastUpdate: string | null }>();
const CACHE_TTL_MS = 300000; // 5 minutes cache (increased from 1 minute)

const getCachedResults = (cacheKey: string): { results: InventoryItem[]; lastUpdate: string | null } | null => {
  const cached = searchCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    console.log('📦 Fast search: using cached results for:', cacheKey);
    return { results: cached.results, lastUpdate: cached.lastUpdate };
  }
  return null;
};

const setCachedResults = (cacheKey: string, results: InventoryItem[], lastUpdate: string | null) => {
  // Limit cache size
  if (searchCache.size > 100) {
    const oldestKey = searchCache.keys().next().value;
    if (oldestKey) searchCache.delete(oldestKey);
  }
  searchCache.set(cacheKey, { results, timestamp: Date.now(), lastUpdate });
};

export const clearFastSearchCache = () => {
  searchCache.clear();
  console.log('🗑️ Fast search cache cleared');
};

/**
 * Fast inventory search using optimized PostgreSQL function
 * - Single database query with all relevance scoring done in PostgreSQL
 * - Uses trigram similarity for fuzzy matching
 * - Includes exact match, starts-with, and contains priorities
 */
export const searchInventoryFast = async (
  searchTerm: string,
  warehouseId: number | null = null,
  maxResults: number = 100
): Promise<{ results: InventoryItem[]; lastUpdate: string | null }> => {
  // Input validation
  const normalizedTerm = searchTerm.trim();
  
  if (normalizedTerm.length < 1) {
    return { results: [], lastUpdate: null };
  }

  // Check cache first
  const cacheKey = `fast:${normalizedTerm.toLowerCase()}-${warehouseId ?? 'all'}`;
  const cached = getCachedResults(cacheKey);
  if (cached) {
    return cached;
  }

  console.log(`🚀 Fast search for: "${normalizedTerm}" warehouse: ${warehouseId}`);
  const startTime = performance.now();

  try {
    // Call the optimized PostgreSQL function
    const { data, error } = await supabase.rpc('search_inventory_fast', {
      search_term: normalizedTerm,
      warehouse_filter: warehouseId,
      max_results: maxResults
    });

    if (error) {
      console.error('Fast search database error:', error);
      throw error;
    }

    const queryTime = performance.now() - startTime;
    console.log(`⚡ Fast search query completed in ${queryTime.toFixed(0)}ms`);

    if (!data || data.length === 0) {
      console.log('No results found');
      return { results: [], lastUpdate: null };
    }

    // Map database results to InventoryItem format
    const results: InventoryItem[] = data.map((item: any) => ({
      product_naam: item.product_naam || '',
      product_id: item.product_id || '',
      bc: item.bc || '',
      locatie: item.locatie || '',
      voorraad: typeof item.voorraad === 'number' ? item.voorraad : parseFloat(item.voorraad) || 0,
      eenheid: item.eenheid || '',
      magazijn: typeof item.magazijn === 'number' ? item.magazijn : parseInt(item.magazijn) || 0,
      moq: typeof item.moq === 'number' ? item.moq : parseInt(item.moq) || 0,
      timestamp_sync: item.timestamp_sync || new Date().toISOString(),
      id: `${item.product_id}-${item.magazijn}`,
      similarity: item.relevance_score ? item.relevance_score / 1000 : 0.5
    }));

    console.log(`✨ Fast search returned ${results.length} results in ${(performance.now() - startTime).toFixed(0)}ms`);

    const lastUpdate = new Date().toISOString();
    
    // Cache results
    if (results.length > 0) {
      setCachedResults(cacheKey, results, lastUpdate);
    }

    return { results, lastUpdate };

  } catch (error) {
    console.error('Fast search error:', error);
    throw error;
  }
};

/**
 * Get available warehouses from the database
 * Uses caching to avoid repeated queries
 */
let warehouseCache: { warehouses: number[]; timestamp: number } | null = null;
const WAREHOUSE_CACHE_TTL_MS = 600000; // 10 minutes

export const getAvailableWarehousesFast = async (): Promise<number[]> => {
  // Check cache first
  if (warehouseCache && Date.now() - warehouseCache.timestamp < WAREHOUSE_CACHE_TTL_MS) {
    console.log('📦 Using cached warehouse list');
    return warehouseCache.warehouses;
  }

  try {
    // Get distinct warehouse values efficiently
    const { data, error } = await supabase
      .from('voorraad_live')
      .select('magazijn')
      .limit(1000);

    if (error) {
      console.error('Error fetching warehouses:', error);
      // Return fallback
      return [2, 5, 7, 8, 12];
    }

    // Extract unique warehouse IDs
    const warehouseSet = new Set<number>();
    data?.forEach(row => {
      const magazijn = typeof row.magazijn === 'number' 
        ? row.magazijn 
        : parseInt(String(row.magazijn).trim(), 10);
      if (!isNaN(magazijn) && magazijn > 0) {
        warehouseSet.add(magazijn);
      }
    });

    const warehouses = Array.from(warehouseSet).sort((a, b) => a - b);
    
    // Cache the result
    warehouseCache = { warehouses, timestamp: Date.now() };
    
    console.log('📊 Available warehouses:', warehouses);
    return warehouses;

  } catch (error) {
    console.error('Error fetching warehouses:', error);
    return [2, 5, 7, 8, 12];
  }
};
